'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { VideoContent, VideoModal } from '@/app_components/Video/VideoModal';
import type { Video } from '@/app_lib/types/Video';

const VideoContentVod = ({
  target,
  videos,
  currentVideoIndex,
  onNextVideo,
}: {
  target: string;
  videos: Video[];
  currentVideoIndex: number;
  onNextVideo: () => void;
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [videoUrl, setVideoUrl] = useState<string | null>(null);

  useEffect(() => {
    const fetchVideoUrl = async () => {
      const data = await fetch(`/api/conferences/getVideoUrl?videoId=${videos[currentVideoIndex].id}`);
      const json = await data.json();
      setVideoUrl(json.url);
    };
    fetchVideoUrl();
  }, [currentVideoIndex, videos]);

  const activeVideo = videos[currentVideoIndex];

  return (
    <VideoContent
      containerRef={containerRef}
      currentVideoIndex={currentVideoIndex}
      onEnd={onNextVideo}
      scrollContainerRef={scrollContainerRef}
      target={target}
      videoId={activeVideo.id}
      videos={videos}
      videoType='vod'
      videoUrl={videoUrl}
    />
  );
};

// NOTE: The state is built into the component, not the content component
// This allows us to preserve the state when the modal is closed and reopened
export const VideoModalVod = ({
  target,
  getVideos,
  forceOpen,
  onClose,
}: {
  target: string;
  getVideos: ({ index }: { index: number }) => Promise<Video[]>;
  forceOpen?: boolean;
  onClose?: () => void;
}) => {
  const [open, setOpen] = useState(false);
  const [currentVideoIndex, setCurrentVideoIndex] = useState(0);
  const [videos, setVideos] = useState<Video[]>([]);

  const onNextVideo = () => {
    setCurrentVideoIndex((prev) => (prev + 1) % (videos.length || 1));
  };

  const handleOnOpen = useCallback(
    async ({ index: newIndex }) => {
      const newVideos = await getVideos({ index: newIndex });
      setVideos(newVideos);
      setOpen(true);
      setCurrentVideoIndex(0);
    },
    [getVideos],
  );

  useEffect(() => {
    if (forceOpen && !open) {
      void handleOnOpen({ index: 0 });
    }
  }, [forceOpen, handleOnOpen, open]);

  const content = (
    <VideoContentVod
      currentVideoIndex={currentVideoIndex}
      onNextVideo={onNextVideo}
      target={target}
      videos={videos}
    />
  );

  return (
    <VideoModal
      content={content}
      currentVideoIndex={currentVideoIndex}
      onClose={onClose}
      onOpen={handleOnOpen}
      open={open}
      setCurrentVideoIndex={setCurrentVideoIndex}
      setOpen={setOpen}
      target={target}
      videos={videos}
      videoType='vod'
    />
  );
};
